import { HiddenContent } from "components/HiddenContent/HiddenContent";
import { TextTruncate } from "../../../../../components";
import { PublishStatus } from "components/common/PublishStatus";
import { Tooltip } from "../../../../../components/common/tooltip";
import { Title } from "../../../../../components/listViewTemplates";
import { PublishedStatusTypes, SortOptions } from "../../../../../enums";
import { type ColumnOption } from "../../../../../interfaces/columnOptions";
import dateTimeUtils from "../../../../../utils/dateTimeUtils";
import { type ScormOverview } from "../../types/models";
import { ThreeDotsItemMenu } from "./ThreeDotsItemMenu";
import cn from "classnames";

export enum Columns {
  FileName = "File Name",
  Publisher = "Publisher",
  Status = "Status",
  Added = "Added",
  Modified = "Modified",
}

export const sortOptions = [
  {
    text: "Most Recent",
    value: SortOptions.CreatedDateDesc,
    default: true,
  },
  {
    text: "Last Modified",
    value: SortOptions.ModifiedDateDesc,
  },
  {
    text: "Sorted A-Z",
    value: SortOptions.Title,
  },
];

export const columnToParamMap: {
  [P in Lowercase<Columns>]: string;
} = {
  "file name": "title",
  publisher: "publisher",
  status: "isDraft",
  modified: "dateModified",
  added: "dateCreated",
};

export const getColumnOptions = ({
  readonly,
  hideDisabled,
  handleEdit,
  handleDelete,
  handleToggleVisibility,
  handlePublish,
}: {
  readonly?: boolean;
  hideDisabled?: boolean;
  handleEdit: (id: number, isDraft: boolean) => void;
  handleDelete: (ids: number[], flowsCount?: number, packsCount?: number) => void;
  handleToggleVisibility: (ids: number[], hidden: boolean) => () => void;
  handlePublish: (id: number, flowsCount: number, hasBeenPublished: boolean) => void;
}): ColumnOption<ScormOverview>[] => {
  return [
    {
      name: Columns.FileName,
      width: 4,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility} showIcon>
          <Title
            url={item.id.toString()}
            title={item.title}
            clamp={2}
            className={cn("normal-color", { "hidden-content": !item.visibility })}
          />
        </HiddenContent>
      ),
    },
    {
      name: Columns.Publisher,
      width: 3,
      isSortable: false,
      render: (item) => (
        <Tooltip
          target={
            <TextTruncate>
              <HiddenContent variant="list" isHidden={!item.visibility}>
                {item.publisher}
              </HiddenContent>
            </TextTruncate>
          }
          content={item.publisher}
          className={cn({ "hidden-content": !item.visibility })}
        />
      ),
    },
    {
      name: Columns.Status,
      width: 3,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          <PublishStatus
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!item.isDraft, item.hasBeenPublished)}
          />
        </HiddenContent>
      ),
    },
    {
      name: Columns.Added,
      width: 2,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          {dateTimeUtils.formatDate(item.dateCreated)}
        </HiddenContent>
      ),
    },
    {
      name: Columns.Modified,
      width: 2,
      isSortable: true,
      render: (item) => (
        <HiddenContent variant="list" isHidden={!item.visibility}>
          {dateTimeUtils.formatDate(item.dateModified)}
        </HiddenContent>
      ),
    },
    {
      name: "",
      width: 1,
      className: "align-right",
      isSortable: false,
      render: (item, isChecked) => {
        return (
          <ThreeDotsItemMenu
            circle
            outlinedEllipsis
            item={item}
            isChecked={isChecked}
            readonly={readonly}
            hasPermission={!readonly}
            hideDisabled={hideDisabled}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleToggleVisibility={handleToggleVisibility}
            handlePublish={handlePublish}
          />
        );
      },
    },
  ];
};
